/**
 * WebSocket Zustand Store
 *
 * State management for WebSocket connection and activity data.
 */

import { createStore } from 'zustand/vanilla';

import { MAX_RECENT_ACTIVITIES } from '@/services/websocket/constants';
import type { ActivityData } from '@/types/websocket';
import { WebSocketConnectionState } from '@/types/websocket';

import type { WebSocketStore } from './types';

// ------------- Initial State -------------

const INITIAL_STATE: Pick<
  WebSocketStore,
  'connectionState' | 'lastError' | 'reconnectAttempts' | 'recentActivities' | 'isHydrated'
> = {
  connectionState: WebSocketConnectionState.DISCONNECTED,
  lastError: null,
  reconnectAttempts: 0,
  recentActivities: [],
  isHydrated: false,
};

// ------------- Helper Functions -------------

/**
 * Find the insertion index for a new activity using binary search
 * Activities are sorted by startedAt descending (most recent first)
 * Returns the index where the new activity should be inserted
 */
function findInsertIndex(activities: ActivityData[], newStartedAt: Date): number {
  let low = 0;
  let high = activities.length;

  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    const activity = activities[mid];
    if (activity === undefined) {
      break;
    }
    const midTime = new Date(activity.startedAt).getTime();
    const newTime = newStartedAt.getTime();

    // For descending order: if newTime > midTime, go left; otherwise go right
    if (newTime > midTime) {
      high = mid;
    } else {
      low = mid + 1;
    }
  }

  return low;
}

/**
 * Add or update an activity in the list using binary insertion
 * Keeps most recent first, limits to MAX_RECENT_ACTIVITIES
 */
function addOrUpdateActivity(
  activities: ActivityData[],
  newActivity: ActivityData,
): ActivityData[] {
  // Check if activity already exists
  const existingIndex = activities.findIndex(a => a.id === newActivity.id);

  if (existingIndex !== -1) {
    // Update existing activity in place
    const existingActivity = activities[existingIndex];
    if (existingActivity === undefined) {
      return activities;
    }

    // If startedAt hasn't changed, just update in place
    if (existingActivity.startedAt === newActivity.startedAt) {
      const updatedActivities = [...activities];
      updatedActivities[existingIndex] = newActivity;
      return updatedActivities;
    }

    // If startedAt changed, remove and re-insert at correct position
    const withoutExisting = activities.filter(a => a.id !== newActivity.id);
    const insertIndex = findInsertIndex(withoutExisting, new Date(newActivity.startedAt));
    const result = [
      ...withoutExisting.slice(0, insertIndex),
      newActivity,
      ...withoutExisting.slice(insertIndex),
    ];

    // Limit to max activities
    if (result.length > MAX_RECENT_ACTIVITIES) {
      return result.slice(0, MAX_RECENT_ACTIVITIES);
    }

    return result;
  }

  // New activity - use binary search to find correct insertion point
  const insertIndex = findInsertIndex(activities, new Date(newActivity.startedAt));
  const result = [
    ...activities.slice(0, insertIndex),
    newActivity,
    ...activities.slice(insertIndex),
  ];

  // Limit to max activities
  if (result.length > MAX_RECENT_ACTIVITIES) {
    return result.slice(0, MAX_RECENT_ACTIVITIES);
  }

  return result;
}

// ------------- Store Factory -------------

/**
 * Creates a Zustand store for WebSocket state management.
 *
 * Features:
 * - Connection state tracking
 * - Error management
 * - Reconnection attempt tracking
 * - Recent activities list (max 50, most recent first)
 * - Binary insertion for O(log n) activity sorting
 */
export function createWebSocketStore() {
  return createStore<WebSocketStore>()((set, get) => ({
    // ------------- Initial State -------------
    ...INITIAL_STATE,

    // ------------- Actions -------------

    setConnectionState: (state: WebSocketConnectionState): void => {
      const { connectionState: currentState } = get();
      if (state === currentState) {
        return;
      }
      set({ connectionState: state });

      // Clear error when connected
      if (state === WebSocketConnectionState.CONNECTED) {
        set({ lastError: null });
      }
    },

    setError: (error: string | null): void => {
      set({ lastError: error });
    },

    incrementReconnectAttempts: (): void => {
      const { reconnectAttempts } = get();
      set({ reconnectAttempts: reconnectAttempts + 1 });
    },

    resetReconnectAttempts: (): void => {
      set({ reconnectAttempts: 0 });
    },

    addActivity: (activity: ActivityData): void => {
      const { recentActivities } = get();
      const updatedActivities = addOrUpdateActivity(recentActivities, activity);
      set({ recentActivities: updatedActivities });
    },

    updateActivity: (id: string, updates: Partial<ActivityData>): void => {
      const { recentActivities } = get();
      const index = recentActivities.findIndex(a => a.id === id);

      if (index === -1) {
        return;
      }

      const updatedActivities = [...recentActivities];
      const existingActivity = updatedActivities[index];
      if (existingActivity !== undefined) {
        updatedActivities[index] = { ...existingActivity, ...updates };
        set({ recentActivities: updatedActivities });
      }
    },

    setHydrated: (hydrated: boolean): void => {
      set({ isHydrated: hydrated });
    },

    reset: (): void => {
      set(INITIAL_STATE);
    },

    handleConnectionStateChange: (
      state: WebSocketConnectionState,
      shouldResetAttempts: boolean,
      shouldIncrementAttempts: boolean,
    ): void => {
      const { connectionState: currentState } = get();
      if (state === currentState) {
        return;
      }

      // Batch all updates into a single state change
      const updates: Partial<WebSocketStore> = {
        connectionState: state,
      };

      // Clear error when connected
      if (state === WebSocketConnectionState.CONNECTED) {
        updates.lastError = null;
      }

      // Handle reconnect attempts
      if (shouldResetAttempts === true) {
        updates.reconnectAttempts = 0;
      } else if (shouldIncrementAttempts === true) {
        const { reconnectAttempts } = get();
        updates.reconnectAttempts = reconnectAttempts + 1;
      }

      set(updates);
    },
  }));
}

// ------------- Store Type -------------

export type WebSocketStoreApi = ReturnType<typeof createWebSocketStore>;
